// Shared scrum board-views component. Extracted verbatim from ScrumManagement so the
// SAME item controls (Accept / Decline / Record acceptance / Decide… / Keep-as-actionable)
// render on both the full Scrum board AND on Today's "My work". Driven by /scrum/v2/board-views.
import { useState } from "react";
import { Target, Briefcase, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SmartDatePicker } from "@/components/SmartDatePicker";

export function fmt(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return d; }
}

// The ONE shared refresh mechanism: any task mutation announces itself on the existing
// scrum:task-completed event; every task list view listens and refetches.
export const announceTaskChange = (task_id) =>
  window.dispatchEvent(new CustomEvent("scrum:task-completed", { detail: { task_id } }));

export const VIEW_META = [
  { key: "awaiting_acceptance", label: "Awaiting my acceptance" },
  { key: "actionable", label: "My actionable tasks" },
  { key: "scheduled", label: "Scheduled" },
  { key: "needs_decision", label: "Needs decision" },
];
const NEEDS_CHOICES = [
  ["convert_to_wait", "Convert to a wait"],
  ["schedule_later", "Schedule for later"],
  ["define_next_action", "Define next action"],
  ["fix_date", "Fix the date"],
  ["close_with_reason", "Close with a reason"],
  ["record_decision", "None of these — record my decision"],
  ["keep_as_actionable", "Keep as actionable task"],
];

// Render where a board-view task came from and why it is here. Every field is optional.
function TaskProvenance({ t }) {
  const parts = [];
  if (t.client_name) parts.push(
    <span className="inline-flex items-center gap-0.5 text-blue-600"><Target className="w-2.5 h-2.5" />{t.client_name}</span>
  );
  if (t.linked_opportunity_id) parts.push(
    <span className="inline-flex items-center gap-0.5 text-indigo-600"><Briefcase className="w-2.5 h-2.5" />Deal</span>
  );
  if (!t.client_name && t.link_type && t.link_type !== "client") parts.push(<span>Internal</span>);
  if (t.task_type && t.task_type !== "general") parts.push(<span className="capitalize">{String(t.task_type).replace(/_/g, " ")}</span>);
  if (t.category) parts.push(<span className="uppercase tracking-wide">{String(t.category).replace(/_/g, " ")}</span>);
  if (t.created_by_name) parts.push(<span>added by {t.created_by_name}</span>);
  if (t.created_at) parts.push(<span>{fmt(t.created_at)}</span>);
  if (parts.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-slate-400" data-testid={`provenance-${t.task_id}`}>
      {parts.map((p, i) => <span key={i} className="inline-flex items-center gap-1">{i > 0 && <span className="text-slate-300">·</span>}{p}</span>)}
    </div>
  );
}

export default function BoardViews({ data, user, onRefresh, apiBase, escalatedInbox = [], onOpenTask }) {
  const staff = ["admin", "managing_partner", "scrum_master"].includes(user?.role);
  const [keepTask, setKeepTask] = useState(null);
  const [keepAction, setKeepAction] = useState("");
  const [keepDue, setKeepDue] = useState("");
  const [keepTitle, setKeepTitle] = useState("");
  const [emailItem, setEmailItem] = useState(null);
  const [decide, setDecide] = useState(null); // { task, choice }
  const [dReason, setDReason] = useState("");
  const [dDate, setDDate] = useState("");
  const [dDep, setDDep] = useState("person");
  const [dCounterparty, setDCounterparty] = useState("");
  const [dTitle, setDTitle] = useState("");
  const [dErr, setDErr] = useState("");
  const post = async (url, body) => {
    const r = await fetch(`${apiBase}${url}`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body || {}) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.detail?.message || e.detail || e.detail?.error || "Action failed"); return false; }
    onRefresh(); announceTaskChange(); return true;
  };
  const openKeep = (t) => { setKeepTask(t); setKeepAction(""); setKeepDue(t.due_date || ""); setKeepTitle(""); };
  const submitKeep = async () => {
    const ok = await post(`/scrum/v2/tasks/${keepTask.task_id}/resolve`, { choice: "keep_as_actionable", payload: { action: keepAction, due_date: keepDue, title: keepTitle || undefined } });
    if (ok) setKeepTask(null);
  };
  const accept = (id) => post(`/scrum/v2/tasks/${id}/accept`, {});
  const decline = (id) => { const why = prompt("Reason for declining?"); if (why) post(`/scrum/v2/tasks/${id}/decline?reason=${encodeURIComponent(why)}`, {}); };
  const recordFor = (id) => { const owner = prompt("Owner user_id who accepted?"); if (!owner) return; const src = prompt("Source (meeting or other)?", "meeting"); if (!src) return; const ref = prompt("Reference (meeting name / note)?", "") || ""; post(`/scrum/v2/tasks/${id}/record-acceptance?owner_id=${encodeURIComponent(owner)}&source=${encodeURIComponent(src)}&reference=${encodeURIComponent(ref)}`, {}); };
  const DATE_CHOICE = { convert_to_wait: "Review / chase date", schedule_later: "Actionable from", fix_date: "Correct due date", define_next_action: "Due date" };
  const CHOICE_LABEL = Object.fromEntries(NEEDS_CHOICES);
  const openDecide = (task, choice) => {
    setDecide({ task, choice }); setDReason(""); setDDate(""); setDDep("person"); setDCounterparty(""); setDTitle(""); setDErr("");
  };
  const submitDecide = async () => {
    const { task, choice } = decide;
    const needsDate = choice in DATE_CHOICE;
    // Every decision carries words. "None of these" and "close with a reason" both refuse an empty reason.
    if (!dReason.trim()) { setDErr("A reason is required — your words are the decision."); return; }
    if (choice === "define_next_action" && !dTitle.trim()) { setDErr("A task title is required."); return; }
    if (needsDate) {
      if (!dDate) { setDErr("A date is required for this action."); return; }
      const d = new Date(dDate);
      if (isNaN(d.getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(dDate)) { setDErr("Enter a valid date (YYYY-MM-DD)."); return; }
    }
    const why = dReason.trim();
    const payload = {};
    if (choice === "convert_to_wait") { payload.dependency_type = dDep; payload.reason = why; payload.counterparty = dCounterparty; payload.start_date = new Date().toISOString().slice(0, 10); payload.review_date = dDate; }
    if (choice === "schedule_later") payload.actionable_from = dDate;
    if (choice === "fix_date") payload.due_date = dDate;
    if (choice === "define_next_action") { payload.title = dTitle.trim(); payload.due_date = dDate; }
    const ok = await post(`/scrum/v2/tasks/${task.task_id}/resolve`, { choice, why, payload });
    if (ok) setDecide(null);
  };
  const views = data?.views || {}; const counts = data?.counts || {};
  return (
    <div className="space-y-4" data-testid="board-views">
      {VIEW_META.map(({ key, label }) => (
        <div key={key} className="border border-slate-200 rounded-lg" data-testid={`view-${key}`}>
          <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-800">{label}</span>
            <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-200" data-testid={`count-${key}`}>{counts[key] ?? 0}</span>
          </div>
          <div className="divide-y">
            {(views[key] || []).length === 0 && <div className="px-3 py-2 text-xs text-slate-400">None</div>}
            {(views[key] || []).map((t) => (
              <div key={t.task_id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`task-${t.task_id}`}>
                <div
                  className="min-w-0 cursor-pointer group"
                  role="button"
                  data-testid={`open-task-${t.task_id}`}
                  onClick={() => onOpenTask?.(t)}
                >
                  <div className="text-sm text-slate-800 truncate group-hover:text-blue-600 group-hover:underline">{t.title}</div>
                  <div className="text-xs text-slate-500">
                    {t.assigned_to_name || "Unassigned"} · {t.status}
                    {" · "}{key === "needs_decision" && t._needs_date ? <span className="text-amber-600">Needs date</span> : (t.due_date || "—")}
                    {t._reason ? <span className="text-amber-600"> · {t._reason}</span> : ""}
                  </div>
                  <TaskProvenance t={t} />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {key === "awaiting_acceptance" && (t.assigned_to_id === user?.user_id || !t.assigned_to_id) && (
                    <>
                      <Button size="sm" className="h-7 text-xs" data-testid={`accept-${t.task_id}`} onClick={() => accept(t.task_id)}>Accept</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`decline-${t.task_id}`} onClick={() => decline(t.task_id)}>Decline</Button>
                    </>
                  )}
                  {key === "awaiting_acceptance" && staff && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`record-accept-${t.task_id}`} onClick={() => recordFor(t.task_id)}>Record acceptance</Button>
                  )}
                  {key === "needs_decision" && (
                    <select className="h-7 text-xs border rounded px-1" data-testid={`resolve-${t.task_id}`} defaultValue="" onChange={(e) => { const v = e.target.value; if (v) { if (v === "keep_as_actionable") { openKeep(t); } else { openDecide(t, v); } e.target.value = ""; } }}>
                      <option value="">Decide…</option>
                      {NEEDS_CHOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  )}
                  {(key === "actionable" || key === "scheduled" || key === "needs_decision") && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`now-with-them-${t.task_id}`}
                      onClick={() => openDecide(t, "convert_to_wait")}>Now with them</Button>
                  )}
                </div>
              </div>
            ))}
            {key === "needs_decision" && staff && escalatedInbox.map((e) => (
              <div key={e.pending_id} className="px-3 py-2 flex items-center justify-between gap-2 bg-rose-50" data-testid={`escalated-inbox-${e.pending_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 truncate flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-rose-500" /> {e.summary}
                  </div>
                  <div className="text-xs text-slate-500">
                    From email · assigned to <b>{e.submitter_name || e.submitter_id}</b> · open {e.business_days_open} business days · awaiting confirmation
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid={`escalated-open-${e.pending_id}`}
                  onClick={() => setEmailItem(e)}>Open email</Button>
              </div>
            ))}
          </div>
        </div>
      ))}
      <Dialog open={!!emailItem} onOpenChange={(v) => { if (!v) setEmailItem(null); }}>
        <DialogContent className="max-w-lg" data-testid="escalated-email-dialog">
          <DialogHeader><DialogTitle>Email awaiting confirmation</DialogTitle></DialogHeader>
          {emailItem && (
            <div className="space-y-2 text-sm">
              <div className="text-xs text-slate-500">From {emailItem.sender_name || emailItem.from} · {emailItem.date || "—"}</div>
              <div className="font-medium">{emailItem.summary}</div>
              <div className="text-xs text-slate-500">Submitter (still owns it): <b>{emailItem.submitter_name || emailItem.submitter_id}</b> · open {emailItem.business_days_open} business days</div>
              <div className="border rounded p-2 bg-slate-50 text-xs whitespace-pre-wrap max-h-64 overflow-auto" data-testid="escalated-email-body">{emailItem.body_preview || "(no body captured)"}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!keepTask} onOpenChange={(v) => { if (!v) setKeepTask(null); }}>
        <DialogContent className="max-w-md" data-testid="keep-actionable-dialog">
          <DialogHeader><DialogTitle>Keep as actionable task</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">"{keepTask?.title}"</p>
          <div className="space-y-3">
            <div><Label>Action you can take now *</Label>
              <Textarea data-testid="keep-action" value={keepAction} onChange={(e) => setKeepAction(e.target.value)} rows={2} placeholder="e.g., Email Demo Contact to propose two dates" /></div>
            <div><Label>Due date *</Label>
              <Input type="date" data-testid="keep-due" value={keepDue} onChange={(e) => setKeepDue(e.target.value)} /></div>
            <div><Label>Clearer title (optional)</Label>
              <Input data-testid="keep-title" value={keepTitle} onChange={(e) => setKeepTitle(e.target.value)} placeholder={keepTask?.title} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKeepTask(null)}>Cancel</Button>
            <Button data-testid="keep-submit" disabled={!keepAction.trim() || !keepDue} onClick={submitKeep}>Keep as actionable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!decide} onOpenChange={(v) => { if (!v) setDecide(null); }}>
        <DialogContent className="max-w-md" data-testid="decide-dialog">
          <DialogHeader><DialogTitle>{decide ? (decide.choice === "convert_to_wait" ? "Now with them — waiting on someone" : CHOICE_LABEL[decide.choice]) : ""}</DialogTitle></DialogHeader>
          {decide && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-500 truncate">{decide.task.title}</div>
              {decide.choice === "define_next_action" && (
                <div>
                  <label className="text-xs text-slate-500">Task title</label>
                  <input className="h-8 text-sm border rounded px-2 w-full" data-testid="decide-title" value={dTitle} onChange={(e) => setDTitle(e.target.value)} />
                </div>
              )}
              <div>
                <label className="text-xs text-slate-500">{decide.choice === "record_decision" ? "Your decision, in your words" : decide.choice === "convert_to_wait" ? "Who / what are we waiting on?" : "Reason"}</label>
                <textarea className="text-sm border rounded px-2 py-1 w-full" rows={2} data-testid="decide-reason" value={dReason} onChange={(e) => setDReason(e.target.value)} />
              </div>
              {decide.choice === "convert_to_wait" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500">Dependency</label>
                    <select className="h-8 text-sm border rounded px-1 w-full" data-testid="decide-dependency" value={dDep} onChange={(e) => setDDep(e.target.value)}>
                      {["person", "client", "payment", "decision", "funds", "event", "other"].map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Counterparty (optional)</label>
                    <input className="h-8 text-sm border rounded px-2 w-full" data-testid="decide-counterparty" value={dCounterparty} onChange={(e) => setDCounterparty(e.target.value)} />
                  </div>
                </div>
              )}
              {decide.choice in DATE_CHOICE && (
                <div>
                  <label className="text-xs text-slate-500">{DATE_CHOICE[decide.choice]}</label>
                  <SmartDatePicker value={dDate} onChange={setDDate} blockWeekends testid="decide-date" />
                </div>
              )}
              {dErr && <p className="text-xs text-rose-600" data-testid="decide-error">{dErr}</p>}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setDecide(null)} data-testid="decide-cancel">Cancel</Button>
                <Button size="sm" onClick={submitDecide} data-testid="decide-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
